import moment from 'moment';

// Convert company name to PascalCase for API
export const toPascalCase = (str) => {
  if (!str || typeof str !== 'string') return '';
  return str
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join('');
};

// Format company name for display (reverse the URL formatting)
export const formatCompanyDisplayName = (companyID) => {
  if (!companyID || typeof companyID !== 'string') return '';
  return companyID
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Convert company name for API (remove dashes and convert to proper case)
export const formatCompanyForApi = (companyID) => {
  if (!companyID || typeof companyID !== 'string') return '';
  return companyID
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join('');
};

// Generate initials from company name
export const generateCompanyInitials = (displayCompanyName) => {
  if (!displayCompanyName || typeof displayCompanyName !== 'string') return '';
  return displayCompanyName
    .split(' ')
    .map((word) => word[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
};

// Calculate date ranges
export const calculateDateRanges = (customStartDate, customEndDate) => ({
  currentMonth: {
    start: moment().startOf('month'),
    end: moment().endOf('month'),
    label: moment().format('MMMM YYYY'),
  },
  previousMonth: {
    start: moment().subtract(1, 'month').startOf('month'),
    end: moment().subtract(1, 'month').endOf('month'),
    label: moment().subtract(1, 'month').format('MMMM YYYY'),
  },
  custom: {
    start: moment(customStartDate),
    end: moment(customEndDate),
    label: 'Custom Range',
  },
});

// Parse external efforts from report
export const parseExternalEfforts = (externalEffortsJson) => {
  if (!externalEffortsJson) return [];

  try {
    return JSON.parse(externalEffortsJson);
  } catch (error) {
    // console.warn('Error parsing external efforts:', error);
    return [];
  }
};

// Get external efforts for a company and date range
export const getExternalEffortsForCompany = (allReports, company, startDate, endDate) => {
  if (!allReports || !startDate || !endDate) return [];

  // Handle both possible data structures: direct array or object with data property
  const reportsArray = Array.isArray(allReports) ? allReports : allReports.data;

  if (!reportsArray || !Array.isArray(reportsArray)) {
    return [];
  }

  // Try multiple company name formats to find the report
  const companyVariants = company
    ? [
        company, // Original format
        toPascalCase(company), // PascalCase format
        formatCompanyForApi(company), // API format
        company.toLowerCase(), // lowercase
        company.toUpperCase(), // uppercase
        company.replace(/\s+/g, ''), // Remove all spaces
        company.replace(/\s+/g, '').toLowerCase(), // Remove spaces and lowercase
        company.replace(/\s+/g, '').toUpperCase(), // Remove spaces and uppercase
      ]
    : [];

  const matchingReports = reportsArray.filter((reportItem) => {
    const reportCompanyVariants = [
      reportItem.company,
      reportItem.company.replace(/\s+/g, ''),
      reportItem.company.toLowerCase(),
      reportItem.company.toUpperCase(),
    ];

    const companyMatches =
      companyVariants.some((variant) => reportCompanyVariants.includes(variant)) ||
      reportCompanyVariants.some((variant) => companyVariants.includes(variant));

    const dateMatches =
      moment(reportItem.startDate).format('YYYY-MM-DD') === startDate && moment(reportItem.endDate).format('YYYY-MM-DD') === endDate;

    return companyMatches && dateMatches;
  });

  // Prefer the report that actually has external efforts (handles duplicate report entries)
  const report = matchingReports.find((r) => r.externalEfforts) || matchingReports[0];

  return parseExternalEfforts(report?.externalEfforts);
};

// Update external effort in a report
export const updateExternalEffortInReport = async (reportsApi, { company, dateRanges, dateFilter, allReports, effortId, updatedFields }) => {
  const selectedRange = dateRanges[dateFilter];
  const startDate = selectedRange.start.format('YYYY-MM-DD');
  const endDate = selectedRange.end.format('YYYY-MM-DD');
  const pascalCaseCompany = toPascalCase(company);

  // Find existing report — prefer the one with external efforts (handles duplicates)
  const matchingReports = allReports?.filter(
    (report) =>
      report.company === pascalCaseCompany &&
      moment(report.startDate).format('YYYY-MM-DD') === startDate &&
      moment(report.endDate).format('YYYY-MM-DD') === endDate
  ) || [];
  const existingReport = matchingReports.find((r) => r.externalEfforts) || matchingReports[0];

  let externalEffortsArray = [];

  // Parse existing external efforts if any
  if (existingReport?.externalEfforts) {
    externalEffortsArray = parseExternalEfforts(existingReport.externalEfforts);
  }

  // Find and update the existing effort
  const effortIndex = externalEffortsArray.findIndex((e) => e.id === effortId);

  if (effortIndex !== -1) {
    // Update existing effort with new fields
    externalEffortsArray[effortIndex] = {
      ...externalEffortsArray[effortIndex],
      ...updatedFields,
      updateTime: new Date().toISOString(),
    };
  } else {
    // If not found, add as new (shouldn't happen normally)
    externalEffortsArray.push({
      id: effortId,
      ...updatedFields,
      updateTime: new Date().toISOString(),
    });
  }

  const externalEffortsJson = JSON.stringify(externalEffortsArray);

  if (existingReport) {
    // Update existing report
    await reportsApi.update(existingReport.id, pascalCaseCompany, startDate, endDate, externalEffortsJson);
  } else {
    // Create new report
    await reportsApi.create(pascalCaseCompany, startDate, endDate, externalEffortsJson);
  }

  return { startDate, endDate, pascalCaseCompany };
};

// Initialize company emails for report generation
export const initializeCompanyEmails = (companies) => {
  const emails = {};

  companies.forEach((company) => {
    const emailMap = new Map();

    company.experts.forEach((expert) => {
      // Add expert email with expert name
      if (expert.email) {
        emailMap.set(expert.email, { email: expert.email, name: expert.name, isCustom: false });
      }

      // Add point of contact emails with their names
      if (expert.pointOfContactJson) {
        try {
          const pocs = JSON.parse(expert.pointOfContactJson);
          pocs.forEach((poc) => {
            if (poc.email) {
              console.log('poc', poc);
              const fullName =
                poc.name ||
                (poc.firstName && poc.lastName ? `${poc.firstName} ${poc.lastName}` : poc.firstName || poc.lastName || 'Point of Contact');
              emailMap.set(poc.email, { email: poc.email, name: fullName, isCustom: false });
            }
          });
        } catch (error) {
          // console.error('Error parsing pointOfContactJson:', error);
        }
      }

      // Add report contact emails with their names
      if (expert.reportsContactsJson) {
        try {
          const reportContacts = JSON.parse(expert.reportsContactsJson);
          reportContacts.forEach((contact) => {
            if (contact.email) {
              const fullName =
                contact.name ||
                (contact.firstName && contact.lastName
                  ? `${contact.firstName} ${contact.lastName}`
                  : contact.firstName || contact.lastName || 'Report Contact');
              emailMap.set(contact.email, { email: contact.email, name: fullName, isCustom: false });
            }
          });
        } catch (error) {
          // console.error('Error parsing reportsContactsJson:', error);
        }
      }
    });

    emails[company.name] = Array.from(emailMap.values());
  });

  return emails;
};
